import { useState, useEffect, useCallback } from 'react';
import {
    Send, Inbox, MailCheck, Reply, AlertCircle, Rocket, Mail,
    LifeBuoy, CheckCircle2, AlertTriangle, Globe, Zap, Pause,
    Play, RefreshCw, Package, ClipboardList, ArrowUpFromLine,
    ShieldCheck, XCircle, Link2, X, TrendingUp, Flame
} from 'lucide-react';

const API = process.env.REACT_APP_ACQUISITION_API || 'http://localhost:5500';

function getToken(): string {
    try { return JSON.parse(localStorage.getItem('acq_auth') || '{}').token || ''; }
    catch { return ''; }
}
function authHeaders(): Record<string, string> {
    return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` };
}

interface WarmupAccount {
    id: string; email: string; provider: string; status: string;
    warmup_score: number; last_error: string | null; created_at: string;
}
interface WarmupLog {
    id: string; account_id: string; sender_email: string;
    action: string; details: string | null; created_at: string;
}
interface WarmupStats {
    sentToday: number; receivedToday: number; rescuedToday: number;
    repliedToday: number; totalAccounts: number; activeAccounts: number;
}
interface CycleState {
    running: boolean;
    startedAt: string | null;
    currentPair: number;
    totalPairs: number;
    phase: string;
    currentSender: string;
    currentOutlook: string;
    lastResult: { sent: number; rescued: number; replied: number; errors: number; finishedAt: string; message?: string } | null;
}
interface Sender {
    email: string; displayName: string; dailySendLimit: number;
    domain: string; sentToday: number; hasPassword: boolean;
}
interface DomainData {
    domain: string; region: string; label: string; senders: Sender[];
}

const ACTION_COLORS: Record<string, string> = { sent: '#6ee7b7', rescued: '#a5b4fc', replied: '#fbbf24', received: '#6ee7b7', error: '#f87171' };
const ActionIcon = ({ action }: { action: string }) => {
    const size = 14;
    switch (action) {
        case 'sent': return <Send size={size} color="#6ee7b7" />;
        case 'rescued': return <Inbox size={size} color="#a5b4fc" />;
        case 'replied': return <Reply size={size} color="#fbbf24" />;
        case 'received': return <MailCheck size={size} color="#6ee7b7" />;
        case 'error': return <XCircle size={size} color="#f87171" />;
        default: return <Mail size={size} color="#94a3b8" />;
    }
};

/* ─── Styles ─── */
const card: React.CSSProperties = {
    background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
    borderRadius: 12, padding: 20,
};
const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
    background: 'var(--surface-default)', color: 'var(--text-primary)',
    border: '1px solid var(--border-default)', borderRadius: 6, outline: 'none',
};
const smallBtn = (bg: string, color: string): React.CSSProperties => ({
    background: `${bg}18`, color, border: `1px solid ${bg}44`,
    borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
});

export function WarmupPage() {
    const [accounts, setAccounts] = useState<WarmupAccount[]>([]);
    const [senders, setSenders] = useState<Sender[]>([]);
    const [senderDomain, setSenderDomain] = useState('');
    const [logs, setLogs] = useState<WarmupLog[]>([]);
    const [stats, setStats] = useState<WarmupStats | null>(null);
    const [cycle, setCycle] = useState<CycleState>({ running: false, startedAt: null, currentPair: 0, totalPairs: 0, phase: 'idle', currentSender: '', currentOutlook: '', lastResult: null });
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState<string | null>(null);
    const [tab, setTab] = useState<'senders' | 'pool' | 'import' | 'logs'>('senders');
    const [outlookCapacity, setOutlookCapacity] = useState<{ totalAccounts: number; availableAccounts: number; totalCapacity: number; remainingCapacity: number } | null>(null);

    // Add form
    const [newEmail, setNewEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [addError, setAddError] = useState('');

    // Bulk import
    const [importText, setImportText] = useState('');
    const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);
    const [importing, setImporting] = useState(false);

    const fetchAll = useCallback(async () => {
        // Skip fetch when browser is offline
        if (!navigator.onLine) return;
        try {
            const [accRes, statsRes, logsRes, domRes, capRes] = await Promise.all([
                fetch(`${API}/warmup/accounts`, { headers: authHeaders() }),
                fetch(`${API}/warmup/stats`, { headers: authHeaders() }),
                fetch(`${API}/warmup/logs?limit=50`, { headers: authHeaders() }),
                fetch(`${API}/domains`, { headers: authHeaders() }),
                fetch(`${API}/dispatch/capacity`, { headers: authHeaders() }).catch(() => null),
            ]);
            setAccounts((await accRes.json()).accounts || []);
            const sd = await statsRes.json();
            setStats(sd.stats || null); setEnabled(sd.enabled || false);
            if (sd.cycle) setCycle(sd.cycle);
            setLogs((await logsRes.json()).logs || []);
            const dd = await domRes.json();
            const allDomains: DomainData[] = dd.domains || [];
            const allSenders = allDomains.flatMap(d => d.senders);
            setSenders(allSenders);
            if (allDomains.length > 0) setSenderDomain(allDomains[0].domain);
            if (capRes) {
                const capData = await capRes.json();
                if (capData.success) setOutlookCapacity(capData);
            }
        } catch {
            // Silently ignore network errors during polling (e.g. offline)
        }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchAll();
        // Poll faster (5s) when cycle is running, otherwise every 30s
        const interval = cycle.running ? 5000 : 30000;
        const t = setInterval(fetchAll, interval);
        // Re-fetch immediately when coming back online
        const onOnline = () => fetchAll();
        window.addEventListener('online', onOnline);
        return () => { clearInterval(t); window.removeEventListener('online', onOnline); };
    }, [fetchAll, cycle.running]);

    async function handleAdd(e: React.FormEvent) {
        e.preventDefault(); setAddError('');
        if (!newEmail || !newPassword) { setAddError('Email and password required'); return; }
        try {
            const res = await fetch(`${API}/warmup/accounts`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({ email: newEmail, password: newPassword, provider: 'outlook' }),
            });
            const data = await res.json();
            if (!data.success) { setAddError(data.error || 'Failed'); return; }
            setNewEmail(''); setNewPassword(''); fetchAll();
        } catch { setAddError('Network error'); }
    }

    async function handleDelete(id: string) {
        if (!window.confirm('Remove this warmup account?')) return;
        await fetch(`${API}/warmup/accounts/${id}`, { method: 'DELETE', headers: authHeaders() });
        fetchAll();
    }

    async function handleTest(id: string) {
        setTesting(id);
        try {
            const data = await (await fetch(`${API}/warmup/accounts/${id}/test`, { method: 'POST', headers: authHeaders() })).json();
            alert(data.imap && data.smtp ? '✅ IMAP + SMTP OK!' : `❌ ${data.error || 'Unknown error'}`);
        } catch { alert('Network error'); }
        setTesting(null); fetchAll();
    }

    async function handleRun() {
        const res = await fetch(`${API}/warmup/run`, { method: 'POST', headers: authHeaders(), body: '{}' });
        const data = await res.json();
        if (data.cycle) setCycle(data.cycle);
        // Trigger a quick re-fetch to get updated state
        setTimeout(fetchAll, 1000);
    }

    async function handleToggle() {
        const newState = !enabled;
        await fetch(`${API}/warmup/toggle`, {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({ enabled: newState, intervalMinutes: 120 }),
        });
        setEnabled(newState);
    }

    async function handleBulkImport() {
        if (!importText.trim()) return;
        setImporting(true); setImportResult(null);
        try {
            const data = await (await fetch(`${API}/warmup/import`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({ text: importText }),
            })).json();
            setImportResult({ imported: data.imported, skipped: data.skipped, errors: data.errors || [] });
            if (data.imported > 0) { setImportText(''); fetchAll(); }
        } catch { setImportResult({ imported: 0, skipped: 0, errors: ['Network error'] }); }
        setImporting(false);
    }

    function scoreBar(score: number) {
        const color = score >= 70 ? '#6ee7b7' : score >= 40 ? '#fbbf24' : '#f87171';
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 60, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)' }}>
                    <div style={{ width: `${score}%`, height: '100%', borderRadius: 3, background: color, transition: 'width 0.5s' }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 28 }}>{score}</span>
            </div>
        );
    }

    function statusDot(status: string) {
        const c = status === 'active' ? '#6ee7b7' : status === 'error' ? '#f87171' : '#94a3b8';
        return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c, boxShadow: `0 0 6px ${c}66` }} />;
    }

    if (loading) return <div style={{ padding: 40, color: 'var(--text-secondary)' }}>Loading...</div>;

    const activeCount = accounts.filter(a => a.status === 'active').length;
    const errorCount = accounts.filter(a => a.status === 'error').length;
    const sendersWithPwd = senders.filter(s => s.hasPassword).length;

    return (
        <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
            {/* ── Header ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ display: 'inline-flex', padding: 6, borderRadius: 10, background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}>
                            <Flame size={20} color="#fff" />
                        </span>
                        Email Warmup Engine
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {senders.length} senders ({senderDomain}) &times; {accounts.length} Outlook receivers &middot;{' '}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: enabled ? '#6ee7b7' : '#94a3b8' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: enabled ? '#6ee7b7' : '#94a3b8', boxShadow: enabled ? '0 0 8px #6ee7b766' : 'none' }} />
                            {enabled ? 'Auto-running' : 'Paused'}
                        </span>
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleToggle} style={{
                        ...smallBtn(enabled ? '#f87171' : '#6366f1', enabled ? '#f87171' : '#a5b4fc'),
                        padding: '6px 14px', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5,
                    }}>
                        {enabled ? <><Pause size={12} /> Stop Auto</> : <><Zap size={12} /> Start Auto</>}
                    </button>
                    <button className="btn btn-primary" onClick={handleRun} disabled={cycle.running || accounts.length === 0}
                        style={{ opacity: cycle.running ? 0.6 : 1, fontSize: 12, padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        {cycle.running ? <><RefreshCw size={12} className="spin" /> Running...</> : <><Play size={12} /> Run Cycle</>}
                    </button>
                </div>
            </div>

            {/* ── Cycle Progress Banner ── */}
            {cycle.running && (
                <div style={{
                    marginBottom: 16, padding: '12px 16px', borderRadius: 10,
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(168,85,247,0.08))',
                    border: '1px solid rgba(99,102,241,0.3)',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{
                                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                background: '#6ee7b7', boxShadow: '0 0 8px #6ee7b766',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }} />
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#a5b4fc' }}>
                                🔥 Warmup Cycle Running
                            </span>
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                            Pair {cycle.currentPair}/{cycle.totalPairs} · {cycle.phase}
                            {cycle.startedAt && ` · ${Math.round((Date.now() - new Date(cycle.startedAt).getTime()) / 60000)}m elapsed`}
                        </span>
                    </div>
                    <div style={{ width: '100%', height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', marginBottom: 8 }}>
                        <div style={{
                            width: `${cycle.totalPairs > 0 ? (cycle.currentPair / cycle.totalPairs) * 100 : 0}%`,
                            height: '100%', borderRadius: 3,
                            background: 'linear-gradient(90deg, #6366f1, #a855f7)',
                            transition: 'width 0.5s ease',
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', gap: 16 }}>
                        {cycle.currentSender && <span>📤 {cycle.currentSender}</span>}
                        {cycle.currentOutlook && <span>→ 📥 {cycle.currentOutlook}</span>}
                    </div>
                </div>
            )}

            {/* ── Last Cycle Result ── */}
            {!cycle.running && cycle.lastResult && (() => {
                const lr = cycle.lastResult!;
                const isNoPairs = lr.sent === 0 && lr.rescued === 0 && lr.replied === 0 && lr.errors === 0;
                const bgColor = isNoPairs ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.06)';
                const borderColor = isNoPairs ? 'rgba(245,158,11,0.25)' : 'rgba(34,197,94,0.2)';
                const textColor = isNoPairs ? '#fbbf24' : '#6ee7b7';
                const icon = isNoPairs ? '⚠️' : '✅';
                return (
                    <div style={{
                        marginBottom: 16, padding: '10px 16px', borderRadius: 10,
                        background: bgColor, border: `1px solid ${borderColor}`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        <span style={{ fontSize: 12, color: textColor, fontWeight: 600 }}>
                            {icon} {lr.message || (
                                isNoPairs
                                    ? 'No pairs available — daily limits reached'
                                    : `${lr.sent} sent · ${lr.rescued} rescued · ${lr.replied} replied`
                            )}
                            {!isNoPairs && lr.errors > 0 && <span style={{ color: '#f87171' }}> · {lr.errors} errors</span>}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                            {new Date(lr.finishedAt).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                );
            })()}

            {/* ── Stats Row ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 10 }}>
                {[
                    { label: 'Senders', value: sendersWithPwd, Icon: Rocket, color: '#c4b5fd' },
                    { label: 'Outlook Pool', value: accounts.length, Icon: Mail, color: '#a5b4fc' },
                    { label: 'Sent Today', value: stats?.sentToday || 0, Icon: Send, color: '#6ee7b7' },
                    { label: 'Rescued', value: stats?.rescuedToday || 0, Icon: LifeBuoy, color: '#fbbf24' },
                    { label: 'Outreach Ready', value: outlookCapacity?.remainingCapacity || 0, Icon: ArrowUpFromLine, color: '#38bdf8' },
                ].map((s, i) => (
                    <div key={i} style={{ ...card, padding: '14px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                            <span style={{ display: 'inline-flex', padding: 6, borderRadius: 8, background: `${s.color}15` }}>
                                <s.Icon size={16} color={s.color} />
                            </span>
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: s.color, margin: '2px 0' }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
                {[
                    { label: 'Replied', value: stats?.repliedToday || 0, Icon: Reply, color: '#c4b5fd' },
                    { label: 'Active', value: activeCount, Icon: CheckCircle2, color: '#6ee7b7' },
                    { label: 'Errors', value: errorCount, Icon: AlertTriangle, color: errorCount > 0 ? '#f87171' : '#94a3b8' },
                    { label: 'Domain', value: senderDomain || '-', Icon: Globe, color: '#38bdf8', isText: true },
                    { label: 'OAuth Ready', value: outlookCapacity?.availableAccounts || 0, Icon: ShieldCheck, color: '#6ee7b7' },
                ].map((s: any, i) => (
                    <div key={i} style={{ ...card, padding: '14px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                            <span style={{ display: 'inline-flex', padding: 6, borderRadius: 8, background: `${s.color}15` }}>
                                <s.Icon size={16} color={s.color} />
                            </span>
                        </div>
                        <div style={{ fontSize: s.isText ? 13 : 24, fontWeight: 700, color: s.color, margin: '2px 0' }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* ── Tab Bar ── */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--border-default)' }}>
                {([
                    { key: 'senders' as const, Icon: Rocket, label: `Sender Pool (${senders.length})` },
                    { key: 'pool' as const, Icon: Mail, label: `Outlook Pool (${accounts.length})` },
                    { key: 'import' as const, Icon: Package, label: 'Bulk Import' },
                    { key: 'logs' as const, Icon: ClipboardList, label: `Activity (${logs.length})` },
                ]).map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={{
                        padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        background: 'transparent', border: 'none',
                        color: tab === t.key ? '#a5b4fc' : 'var(--text-dim)',
                        borderBottom: tab === t.key ? '2px solid #6366f1' : '2px solid transparent',
                        transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}>
                        <t.Icon size={14} /> {t.label}
                    </button>
                ))}
            </div>

            {/* ── Senders Tab (lawhelper.lat accounts that need warming) ── */}
            {tab === 'senders' && (
                <div style={{ ...card, marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: 15, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}><Rocket size={16} color="#a5b4fc" /> Sender Accounts — Need Warming</h3>
                            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-dim)' }}>
                                These {senderDomain} accounts send warmup emails to the Outlook pool to build domain reputation.
                            </p>
                        </div>
                        <div style={{ ...smallBtn('#6366f1', '#a5b4fc'), padding: '6px 12px', fontSize: 11, fontWeight: 600 }}>
                            {senderDomain}
                        </div>
                    </div>
                    {senders.length === 0 ? (
                        <p style={{ color: 'var(--text-dim)', fontSize: 13, textAlign: 'center', padding: 30 }}>
                            No sender accounts configured. Add domains in the Domains page first.
                        </p>
                    ) : (
                        <div>
                            {senders.map((s, i) => {
                                const pct = s.dailySendLimit > 0 ? Math.round(s.sentToday / s.dailySendLimit * 100) : 0;
                                const barColor = pct >= 80 ? '#f87171' : pct >= 50 ? '#fbbf24' : '#6ee7b7';
                                return (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 12px',
                                        borderBottom: i < senders.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                                        transition: 'background 0.15s',
                                    }}>
                                        <div style={{
                                            width: 36, height: 36, borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0,
                                        }}>
                                            {s.displayName?.charAt(0)?.toUpperCase() || s.email.charAt(0).toUpperCase()}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>
                                                {s.email}
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                                                {s.displayName} · Limit: {s.dailySendLimit}/day
                                            </div>
                                        </div>
                                        <div style={{ width: 140, flexShrink: 0 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-dim)', marginBottom: 3 }}>
                                                <span>Today</span>
                                                <span style={{ color: barColor, fontWeight: 700 }}>{s.sentToday}/{s.dailySendLimit}</span>
                                            </div>
                                            <div style={{ width: '100%', height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)' }}>
                                                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: barColor, transition: 'width 0.5s' }} />
                                            </div>
                                        </div>
                                        <div style={{ flexShrink: 0, display: 'flex', gap: 4 }}>
                                            <span style={{
                                                padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                                background: s.hasPassword ? '#6ee7b722' : '#f8717122',
                                                color: s.hasPassword ? '#6ee7b7' : '#f87171',
                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                            }}>
                                                {s.hasPassword ? <><CheckCircle2 size={10} /> Ready</> : <><XCircle size={10} /> No PWD</>}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div style={{ marginTop: 16, padding: '12px 14px', background: 'rgba(99,102,241,0.06)', borderRadius: 8, fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <AlertCircle size={14} color="#a5b4fc" style={{ flexShrink: 0, marginTop: 2 }} />
                        <span><strong style={{ color: '#a5b4fc' }}>How it works:</strong> Each sender sends warmup emails to random Outlook accounts in the pool.
                        Outlook accounts auto-rescue from junk, mark as read, and reply via Graph API — building {senderDomain}'s domain reputation.</span>
                    </div>
                </div>
            )}

            {/* ── Pool Tab ── */}
            {tab === 'pool' && (
                <div style={{ ...card, marginBottom: 16 }}>
                    {/* Outreach capacity banner */}
                    {outlookCapacity && outlookCapacity.availableAccounts > 0 && (
                        <div style={{
                            marginBottom: 16, padding: '12px 16px', borderRadius: 10,
                            background: 'linear-gradient(135deg, rgba(56,189,248,0.08), rgba(99,102,241,0.06))',
                            border: '1px solid rgba(56,189,248,0.2)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ display: 'inline-flex', padding: 6, borderRadius: 8, background: 'rgba(56,189,248,0.15)' }}>
                                    <ArrowUpFromLine size={16} color="#38bdf8" />
                                </span>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#38bdf8' }}>
                                        📬 Dual Purpose Pool — Also Used for Outreach
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                                        {outlookCapacity.availableAccounts} accounts with OAuth can send outreach via Graph API.
                                        Smart routing sends Outlook→Outlook for maximum inbox placement.
                                    </div>
                                </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontSize: 20, fontWeight: 700, color: '#38bdf8' }}>{outlookCapacity.remainingCapacity}</div>
                                <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>sends left today</div>
                            </div>
                        </div>
                    )}

                    {/* Quick add row */}
                    <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                        <input type="email" placeholder="Email" value={newEmail} onChange={e => setNewEmail(e.target.value)} style={{ ...inputStyle, flex: 2 }} />
                        <input type="password" placeholder="Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={{ ...inputStyle, flex: 2 }} />
                        <button type="submit" className="btn btn-primary" style={{ whiteSpace: 'nowrap', fontSize: 12, padding: '8px 14px' }}>+ Add</button>
                        {addError && <span style={{ color: '#f87171', fontSize: 11 }}>{addError}</span>}
                    </form>

                    {accounts.length === 0 ? (
                        <p style={{ color: 'var(--text-dim)', fontSize: 13, textAlign: 'center', padding: 30 }}>
                            No accounts yet. Add manually above or use Bulk Import tab.
                        </p>
                    ) : (
                        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                        <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Status</th>
                                        <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Email</th>
                                        <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Score</th>
                                        <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Added</th>
                                        <th style={{ textAlign: 'right', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accounts.map(acc => (
                                        <tr key={acc.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                            <td style={{ padding: '8px 10px' }}>{statusDot(acc.status)}</td>
                                            <td style={{ padding: '8px 10px', color: 'var(--text-primary)' }}>{acc.email}</td>
                                            <td style={{ padding: '8px 10px' }}>{scoreBar(acc.warmup_score)}</td>
                                            <td style={{ padding: '8px 10px', color: 'var(--text-dim)', fontSize: 11 }}>
                                                {new Date(acc.created_at).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                                            </td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                                                <button onClick={() => handleTest(acc.id)} disabled={testing === acc.id}
                                                    style={{ ...smallBtn('#6366f1', '#a5b4fc'), marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                    {testing === acc.id ? '...' : <><Link2 size={10} /> Test</>}
                                                </button>
                                                <button onClick={() => handleDelete(acc.id)} style={{ ...smallBtn('#f87171', '#f87171'), display: 'inline-flex', alignItems: 'center' }}><X size={10} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── Import Tab ── */}
            {tab === 'import' && (
                <div style={{ ...card }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: 15, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}><Package size={16} color="#a5b4fc" /> Bulk Import Outlook Accounts</h3>
                    <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '0 0 12px' }}>
                        Paste accounts in format: <code style={{ background: 'rgba(99,102,241,0.15)', padding: '2px 6px', borderRadius: 4, color: '#a5b4fc' }}>
                        email----password----clientId----oauthToken</code> (one per line)
                    </p>
                    <textarea
                        value={importText}
                        onChange={e => setImportText(e.target.value)}
                        placeholder={`RylesLocks45@outlook.com----kQz1xuH0cd8E----9e5f94bc-e8a4-4e73-b8be-63364c29d753----M.C511_BL2...\nDillinLimbach834@outlook.com----E6bme8ontBNd----9e5f94bc----M.C522_SN1...\n# Lines starting with # are ignored`}
                        style={{
                            ...inputStyle, height: 180, resize: 'vertical', fontFamily: 'monospace', fontSize: 11,
                            lineHeight: 1.6, marginBottom: 12,
                        }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button onClick={handleBulkImport} disabled={importing || !importText.trim()}
                            className="btn btn-primary" style={{ fontSize: 12, padding: '8px 20px' }}>
                            {importing ? 'Importing...' : `Import ${importText.split('\n').filter(l => l.trim() && !l.startsWith('#')).length} Accounts`}
                        </button>
                        {importResult && (
                            <span style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: '#6ee7b7', display: 'inline-flex', alignItems: 'center', gap: 3 }}><CheckCircle2 size={12} /> {importResult.imported} imported</span>
                                {importResult.skipped > 0 && <span style={{ color: '#fbbf24', marginLeft: 8 }}>{importResult.skipped} duplicates</span>}
                                {importResult.errors.length > 0 && <span style={{ color: '#f87171', marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 3 }}><XCircle size={12} /> {importResult.errors.length} errors</span>}
                            </span>
                        )}
                    </div>
                    {importResult && importResult.errors.length > 0 && (
                        <div style={{ marginTop: 12, padding: 10, background: 'rgba(248,113,113,0.08)', borderRadius: 6, fontSize: 11, color: '#f87171' }}>
                            {importResult.errors.map((e, i) => <div key={i}>{e}</div>)}
                        </div>
                    )}
                </div>
            )}

            {/* ── Logs Tab ── */}
            {tab === 'logs' && (
                <div style={{ ...card }}>
                    {logs.length === 0 ? (
                        <p style={{ color: 'var(--text-dim)', fontSize: 13, textAlign: 'center', padding: 20 }}>No warmup activity yet.</p>
                    ) : (
                        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                            {logs.map(log => {
                                const time = new Date(log.created_at).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
                                const date = new Date(log.created_at).toLocaleDateString('en', { month: 'short', day: 'numeric' });
                                return (
                                    <div key={log.id} style={{
                                        display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0',
                                        borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 12,
                                    }}>
                                        <span style={{ color: 'var(--text-dim)', width: 75, flexShrink: 0, fontSize: 11 }}>{date} {time}</span>
                                        <span style={{ width: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><ActionIcon action={log.action} /></span>
                                        <span style={{
                                            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                                            background: `${ACTION_COLORS[log.action] || '#94a3b8'}22`,
                                            color: ACTION_COLORS[log.action] || '#94a3b8',
                                            textTransform: 'uppercase', width: 54, textAlign: 'center', flexShrink: 0,
                                        }}>{log.action}</span>
                                        <span style={{ color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {log.details || log.sender_email}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ── How It Works ── */}
            <div style={{ ...card, marginTop: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 14 }}>
                    {[
                        { Icon: Send, title: 'Warmup', desc: `${senderDomain} → Outlook`, color: '#6ee7b7' },
                        { Icon: LifeBuoy, title: 'Rescue', desc: 'Junk → Inbox (IMAP)', color: '#a5b4fc' },
                        { Icon: Reply, title: 'Reply', desc: 'Graph API auto-reply', color: '#fbbf24' },
                        { Icon: TrendingUp, title: 'Reputation', desc: 'Score increases over time', color: '#c4b5fd' },
                        { Icon: ArrowUpFromLine, title: 'Outreach', desc: 'Outlook → Leads (Graph)', color: '#38bdf8' },
                    ].map((s, i) => (
                        <div key={i} style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                                <span style={{ display: 'inline-flex', padding: 8, borderRadius: 10, background: `${s.color}15` }}>
                                    <s.Icon size={20} color={s.color} />
                                </span>
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>{s.title}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{s.desc}</div>
                        </div>
                    ))}
                </div>
                <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-dim)', borderTop: '1px solid var(--border-default)', paddingTop: 10 }}>
                    {senders.length} Senders × {accounts.length} Outlook = {senders.length * accounts.length} warmup pairs
                    {outlookCapacity && <> · <span style={{ color: '#38bdf8' }}>{outlookCapacity.availableAccounts} Outlook accounts ready for outreach ({outlookCapacity.remainingCapacity} sends/day)</span></>}
                </div>
            </div>
        </div>
    );
}
